/**
 * Krok 4: Portfolio / Realizacje — zdjęcia z opisami.
 * Reużywa ImageUploader (gallery), a portfolio przechowuje pary {image, caption}.
 */
import { useState } from 'react';
import { ImageUploader } from '@/components/ImageUploader';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Camera, CheckCircle2, Trash2 } from 'lucide-react';
import { getImageUrl } from '@/utils/imageUrl';
import { ImageSeoBadge } from '@/components/ImageSeoBadge';

export default function Step4Portfolio({ data, updateData }) {
  const portfolio = data.portfolio || [];
  const [captionDraft, setCaptionDraft] = useState({}); // {index: text}

  const handleCoverChange = (url) => {
    updateData({ cover_photo: url || '' });
  };

  const handleGalleryChange = (gal) => {
    // gal jest płaską listą URL-i; konwertujemy na portfolio (zachowując istniejące captionsy)
    const next = gal.map((url, idx) => {
      const existing = portfolio.find((p) => (p.image || p) === url);
      return existing ? existing : { image: url, caption: '' };
    });
    updateData({ portfolio: next, photos: gal });
  };

  const updateCaption = (idx, text) => {
    const next = portfolio.map((p, i) => (i === idx ? { ...p, caption: text } : p));
    updateData({ portfolio: next });
  };

  const removeOne = (idx) => {
    const next = portfolio.filter((_, i) => i !== idx);
    updateData({ portfolio: next, photos: next.map((p) => p.image || p) });
  };

  return (
    <div className="space-y-8" data-testid="business-wizard-v2-step-portfolio">
      <header>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-orange-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-orange-500" />
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-orange-600">Krok 4 z 7</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Pokaż swoje realizacje</h2>
        <p className="text-sm text-gray-500 mt-1">Klienci kupują oczami — dodaj zdjęcia efektów swojej pracy.</p>
      </header>

      <ImageSeoBadge />

      {/* Cover */}
      <section>
        <Label className="text-base font-semibold text-gray-900 mb-2 block">Zdjęcie w tle (cover)</Label>
        <ImageUploader
          coverImage={data.cover_photo}
          gallery={portfolio.map((p) => p.image || p)}
          onCoverChange={handleCoverChange}
          onGalleryChange={handleGalleryChange}
          maxGalleryImages={20}
        />
      </section>

      {/* Captions */}
      {portfolio.length > 0 && (
        <section>
          <Label className="text-base font-semibold text-gray-900 mb-1 block">Podpisy realizacji (opcjonalnie)</Label>
          <p className="text-xs text-gray-500 mb-3">Krótki opis przy zdjęciu (np. „Strzyżenie męskie + broda · 60 min”).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {portfolio.map((p, idx) => (
              <div key={idx} className="flex gap-2 items-start bg-gray-50 rounded-xl p-2.5 border border-gray-100" data-testid={`bwv2-portfolio-${idx}`}>
                <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-200">
                  <img src={getImageUrl(p.image || p) || (p.image || p)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <Input
                    value={p.caption || ''}
                    onChange={(e) => updateCaption(idx, e.target.value)}
                    placeholder="Opis realizacji"
                    className="bg-white"
                    data-testid={`bwv2-portfolio-caption-${idx}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeOne(idx)}
                  data-testid={`bwv2-portfolio-remove-${idx}`}
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {portfolio.length === 0 && (
        <div className="border-2 border-dashed border-orange-200 rounded-2xl p-6 text-center bg-orange-50/30">
          <Camera className="w-10 h-10 mx-auto text-orange-400 mb-2" />
          <p className="text-sm text-gray-600">Dodaj min. 3 zdjęcia realizacji aby zwiększyć zaufanie klientów.</p>
        </div>
      )}
    </div>
  );
}
